import { useState } from 'react';
import Button from '@/components/base/Button';
import { discoveryPrompts } from '@/mocks/creators';
import { useTypewriter } from '@/components/motion/motionHelpers';
import { RotatingWords } from '@/components/motion/Motion';

/** What the AI says it's doing while it reads a brief. */
const THINKING_STEPS = ['Reading your brief…', 'Picking markets and platforms…', 'Checking engagement and rates…', 'Ranking creators by fit…'];

/** Example searches the placeholder types out, one after another. */
const EXAMPLES = [
  'Skincare creators in Kenya with 5%+ engagement for a vitamin-C launch',
  'Fintech explainers on YouTube with over 50K followers in Nigeria',
  'Food creators on TikTok in Nairobi who post in Swahili',
  'Fitness creators in Uganda with a verified wallet, under KES 40,000',
];

interface SearchPromptProps {
  /** Applies the prompt as filters; says whether AI or keyword matching read it. */
  onSearch: (prompt: string) => Promise<'ai' | 'keywords'>;
}

export default function SearchPrompt({ onSearch }: SearchPromptProps) {
  const [prompt, setPrompt] = useState('');
  const [thinking, setThinking] = useState(false);
  const [source, setSource] = useState<'ai' | 'keywords' | null>(null);
  const [focused, setFocused] = useState(false);
  const example = useTypewriter(EXAMPLES, focused || prompt.length > 0);

  const run = async (value: string) => {
    const text = value.trim();
    if (!text) return;
    setThinking(true);
    try {
      setSource(await onSearch(text));
    } finally {
      setThinking(false);
    }
  };

  return (
    <div className={`ai-box rounded-lg border border-background-200 bg-background-50 p-5 md:p-6 ${thinking ? 'is-thinking' : ''}`}>
      <div className="flex items-center gap-2">
        <span className="ai-spark w-7 h-7 flex items-center justify-center rounded-md bg-accent-500 text-background-50 text-base">
          <i className="ri-sparkling-2-line" />
        </span>
        <div>
          <h2 className="font-heading text-sm font-semibold text-foreground-950">
            Describe the creator you need
          </h2>
          <p className="text-xs text-foreground-500">
            Plain language works best — CPaaS turns it into filters and a ranked pool.
          </p>
        </div>
      </div>

      <div className="mt-4 flex flex-col gap-3 sm:flex-row sm:items-end">
        <div className="flex-1">
          <textarea
            value={prompt}
            onChange={(e) => setPrompt(e.target.value)}
            onFocus={() => setFocused(true)}
            onBlur={() => setFocused(false)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                void run(prompt);
              }
            }}
            rows={2}
            maxLength={500}
            placeholder={focused ? 'Describe the creators you need…' : `e.g. ${example}`}
            aria-label="Describe the creator you need"
            className="w-full resize-none rounded-md border border-background-300 bg-background-50 px-3 py-2.5 text-sm text-foreground-800 placeholder:text-foreground-400 focus:border-primary-400 focus:outline-none focus:ring-2 focus:ring-primary-400/30"
          />
        </div>
        <Button
          size="lg"
          icon={thinking ? 'ri-loader-4-line' : 'ri-search-eye-line'}
          onClick={() => void run(prompt)}
          disabled={thinking}
          className={`join-shine sm:mb-0.5 ${thinking ? '[&_i]:animate-spin' : ''}`}
        >
          {thinking ? 'Matching creators...' : 'Find creators'}
        </Button>
      </div>

      {thinking ? (
        <p className="mt-3 flex items-center gap-2 text-sm font-medium text-foreground-800" role="status" aria-label="Matching creators">
          <span className="relative flex h-2 w-2" aria-hidden="true">
            <span className="absolute inline-flex h-full w-full rounded-full bg-accent-400 opacity-75 motion-safe:animate-ping" />
            <span className="relative inline-flex h-2 w-2 rounded-full bg-accent-500" />
          </span>
          <RotatingWords words={THINKING_STEPS} className="is-dark" every={1100} delay={0} />
        </p>
      ) : null}
      <div className={`mt-3 flex-wrap items-center gap-2 ${thinking ? 'hidden' : 'flex'}`}>
        <span className="text-xs text-foreground-400">Try:</span>
        {discoveryPrompts.map((suggestion) => (
          <button
            key={suggestion}
            type="button"
            onClick={() => {
              setPrompt(suggestion);
              void run(suggestion);
            }}
            className="rounded-full border border-background-200 bg-background-100 px-3 py-1 text-xs text-foreground-600 transition-colors cursor-pointer hover:border-secondary-300 hover:bg-secondary-50 hover:text-secondary-900"
          >
            {suggestion}
          </button>
        ))}
      </div>
      {source && !thinking ? (
        <p className="mt-2 text-[11px] text-foreground-400">
          {source === 'ai'
            ? 'AI turned your description into the filters below; adjust them as you like.'
            : 'Filters matched from keywords (AI wasn’t available); adjust them as you like.'}
        </p>
      ) : null}
    </div>
  );
}